import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/app/UI";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardList, Briefcase, ListChecks, HardHat, LifeBuoy, Search, ChevronRight, AlertTriangle } from "lucide-react";

const SECTIONS = [
  { key: "leads", label: "My Leads", icon: ClipboardList },
  { key: "projects", label: "My Projects", icon: Briefcase },
  { key: "milestone_activities", label: "Milestone Activities", icon: ListChecks },
  { key: "work_items", label: "Work Items", icon: HardHat },
  { key: "tickets", label: "Service Tickets", icon: LifeBuoy },
];

const isOverdueIso = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
};

export default function MyAssignments() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("leads");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/me/assignments").then((r) => setData(r.data)).catch(() => setData({ totals: {}, leads: [], projects: [], milestone_activities: [], work_items: [], tickets: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const rows = data[tab] || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(needle));
  }, [data, tab, q]);

  if (!data) return <div className="p-8 label-eyebrow">Loading your assignments…</div>;

  return (
    <div className="px-4 sm:px-8 py-6 space-y-6" data-testid="my-assignments-page">
      <PageHeader
        eyebrow="Your Work"
        title="My Assignments"
        subtitle={`Everything assigned to ${user?.name || "you"} — leads, projects, milestone activities, work items, and tickets.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {SECTIONS.map((s) => (
          <StatCard
            key={s.key}
            label={s.label}
            value={data.totals?.[s.key] ?? 0}
            accent={tab === s.key ? "#002FA7" : undefined}
          />
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none border border-[#E5E7EB] bg-white p-0 flex-wrap h-auto">
          {SECTIONS.map((s) => (
            <TabsTrigger
              key={s.key}
              value={s.key}
              className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-4 h-10 flex items-center gap-1.5"
              data-testid={`assign-tab-${s.key}`}
            >
              <s.icon className="w-3.5 h-3.5" /> {s.label} ({data.totals?.[s.key] ?? 0})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search within this view…"
          className="pl-9 h-10 rounded-none"
          data-testid="assign-search"
        />
      </div>

      <SectionRenderer tab={tab} rows={filtered} />
    </div>
  );
}

function SectionRenderer({ tab, rows }) {
  if (!rows.length) {
    return (
      <div className="border border-[#E5E7EB] bg-white p-12 text-center text-sm text-[#9CA3AF]" data-testid="assign-empty">
        Nothing assigned to you here right now.
      </div>
    );
  }
  switch (tab) {
    case "leads":              return <LeadsTable rows={rows} />;
    case "projects":           return <ProjectsTable rows={rows} />;
    case "milestone_activities": return <MilestonesTable rows={rows} />;
    case "work_items":         return <WorkItemsTable rows={rows} />;
    case "tickets":            return <TicketsTable rows={rows} />;
    default: return null;
  }
}

const TH = ({ children, className = "" }) => (
  <th className={`text-left px-4 py-2 text-[10px] uppercase tracking-widest font-bold text-[#4B5563] ${className}`}>{children}</th>
);
const TD = ({ children, className = "" }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
const TableShell = ({ headers, children }) => (
  <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
    <table className="w-full text-sm" style={{ minWidth: 800 }}>
      <thead className="border-b border-[#E5E7EB] bg-[#F9FAFB]"><tr>{headers.map((h, i) => <TH key={i}>{h}</TH>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

function LeadsTable({ rows }) {
  return (
    <TableShell headers={["Lead", "Phone", "Stage", "Next Follow-up", "Bill / mo", ""]}>
      {rows.map((r) => {
        const due = r.next_followup_at || r.next_followup_date;
        const overdue = isOverdueIso(due);
        return (
          <tr key={r.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`assign-lead-${r.id}`}>
            <TD><Link to={`/leads/${r.id}`} className="font-bold hover:underline">{r.name}</Link></TD>
            <TD className="text-xs text-[#4B5563]">{r.phone || "—"}</TD>
            <TD><Chip>{r.stage || r.status || "—"}</Chip></TD>
            <TD className={`text-xs ${overdue ? "text-[#DC2626] font-bold" : "text-[#4B5563]"}`}>
              {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
              {due ? fmtDate(due) : "—"}
            </TD>
            <TD className="text-xs">₹{(r.monthly_bill_inr || 0).toLocaleString("en-IN")}</TD>
            <TD className="text-right"><Link to={`/leads/${r.id}`} className="text-[#002FA7]"><ChevronRight className="w-4 h-4" /></Link></TD>
          </tr>
        );
      })}
    </TableShell>
  );
}

function ProjectsTable({ rows }) {
  return (
    <TableShell headers={["Project", "Location", "Capacity", "Stage", "Role(s)", "Done", ""]}>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`assign-project-${r.id}`}>
          <TD>
            <div className="label-eyebrow text-[9px]">{r.qr_code}</div>
            <Link to={`/projects/${r.id}`} className="font-bold hover:underline">{r.name}</Link>
          </TD>
          <TD className="text-xs text-[#4B5563]">{r.location || "—"}</TD>
          <TD className="text-xs">{r.capacity_kw ? `${r.capacity_kw} kW` : "—"}</TD>
          <TD><Chip>{r.current_stage || "—"}</Chip></TD>
          <TD className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
            {(r.roles || []).map((x) => x.replace("_", " ")).join(" · ") || "—"}
          </TD>
          <TD className="text-xs font-bold">{r.completion_pct ?? 0}%</TD>
          <TD className="text-right"><Link to={`/projects/${r.id}`} className="text-[#002FA7]"><ChevronRight className="w-4 h-4" /></Link></TD>
        </tr>
      ))}
    </TableShell>
  );
}

function MilestonesTable({ rows }) {
  return (
    <TableShell headers={["Project", "Stage", "Activity", "Due", "Notes", ""]}>
      {rows.map((r, i) => {
        const overdue = isOverdueIso(r.due_date);
        return (
          <tr key={`${r.project_id}-${r.sub_key}-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`assign-activity-${r.project_id}-${r.sub_key}`}>
            <TD><Link to={`/projects/${r.project_id}`} className="font-bold hover:underline">{r.project_name}</Link></TD>
            <TD className="text-xs text-[#4B5563]">{r.stage_label}</TD>
            <TD className="text-sm">{r.sub_label}</TD>
            <TD className={`text-xs ${overdue ? "text-[#DC2626] font-bold" : "text-[#4B5563]"}`}>
              {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
              {r.due_date ? fmtDate(r.due_date) : "—"}
            </TD>
            <TD className="text-[11px] text-[#4B5563] max-w-xs truncate">{r.notes || "—"}</TD>
            <TD className="text-right"><Link to={`/projects/${r.project_id}`} className="text-[#002FA7]"><ChevronRight className="w-4 h-4" /></Link></TD>
          </tr>
        );
      })}
    </TableShell>
  );
}

function WorkItemsTable({ rows }) {
  return (
    <TableShell headers={["Project", "Work Type", "Planned Date", "Status", ""]}>
      {rows.map((r, i) => {
        const overdue = isOverdueIso(r.planned_date) && r.status !== "completed";
        return (
          <tr key={`${r.project_id}-${r.stage_key}-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`assign-work-${r.project_id}-${r.stage_key}`}>
            <TD>
              <div className="label-eyebrow text-[9px]">{r.qr_code}</div>
              <Link to={`/projects/${r.project_id}`} className="font-bold hover:underline">{r.project_name}</Link>
            </TD>
            <TD className="text-sm">{r.stage_key}</TD>
            <TD className={`text-xs ${overdue ? "text-[#DC2626] font-bold" : "text-[#4B5563]"}`}>
              {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
              {r.planned_date ? fmtDate(r.planned_date) : "—"}
            </TD>
            <TD><Chip>{(r.status || "pending").replace("_", " ")}</Chip></TD>
            <TD className="text-right"><Link to="/work-scheduler" className="text-[#002FA7]"><ChevronRight className="w-4 h-4" /></Link></TD>
          </tr>
        );
      })}
    </TableShell>
  );
}

function TicketsTable({ rows }) {
  return (
    <TableShell headers={["Title", "Customer", "Priority", "Status", "Due", "Opened", ""]}>
      {rows.map((r) => {
        const overdue = isOverdueIso(r.due_date);
        return (
          <tr key={r.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`assign-ticket-${r.id}`}>
            <TD><Link to={`/tickets?id=${r.id}`} className="font-bold hover:underline">{r.title || r.id}</Link></TD>
            <TD className="text-xs">{r.customer_name || "—"}</TD>
            <TD className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">{r.priority || "—"}</TD>
            <TD><Chip>{r.status || "—"}</Chip></TD>
            <TD className={`text-xs ${overdue ? "text-[#DC2626] font-bold" : "text-[#4B5563]"}`}>
              {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
              {r.due_date ? fmtDate(r.due_date) : "—"}
            </TD>
            <TD className="text-xs text-[#4B5563]">{fmtDate(r.created_at) || "—"}</TD>
            <TD className="text-right"><Link to={`/tickets?id=${r.id}`} className="text-[#002FA7]"><ChevronRight className="w-4 h-4" /></Link></TD>
          </tr>
        );
      })}
    </TableShell>
  );
}

const Chip = ({ children }) => (
  <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#F3F4F6] text-[#0a0a0a]">{children}</span>
);
